import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, User, Clock, FileText, BookOpen, FileSpreadsheet, Download, ArrowRight, Search, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Resource {
  id: string;
  type: 'article' | 'guide' | 'case-study';
  title: string;
  description: string;
  date: string;
  author: string;
  readTime: string;
  image: string;
  category: string;
  downloadUrl?: string;
}

const Resources: React.FC = () => {
  useEffect(() => {
    document.title = 'Ressources | Proxy Emobility';
  }, []);

  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const resources: Resource[] = [
    {
      id: 'article-1',
      type: 'article',
      title: 'La Révolution de la Mobilité Électrique en Afrique',
      description: 'Découvrez comment les motos électriques transforment le paysage urbain africain, réduisent les émissions et créent de nouvelles opportunités économiques.',
      date: '15 Mai 2024',
      author: 'Dr. Amadou Diallo',
      readTime: '8 min',
      image: 'https://images.pexels.com/photos/3819830/pexels-photo-3819830.jpeg',
      category: 'Tendances'
    },
    {
      id: 'guide-1',
      type: 'guide',
      title: 'Guide Complet des Motos Électriques',
      description: 'Tout ce que vous devez savoir sur les motos électriques : technologie, autonomie, recharge, entretien et conseils d\'utilisation.',
      date: '10 Mai 2024',
      author: 'Sarah Koné',
      readTime: '15 min',
      image: 'https://images.pexels.com/photos/13035553/pexels-photo-13035553.jpeg',
      category: 'Guide Pratique',
      downloadUrl: '/guides/motos-electriques.pdf'
    },
    {
      id: 'article-2',
      type: 'article',
      title: 'Avantages Économiques des Motos Électriques',
      description: 'Analyse détaillée des économies réalisées avec une moto électrique : coûts d\'exploitation, maintenance, durée de vie et retour sur investissement.',
      date: '5 Mai 2024',
      author: 'Marie Touré',
      readTime: '10 min',
      image: 'https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg',
      category: 'Économie'
    },
    {
      id: 'case-study-1',
      type: 'case-study',
      title: 'Success Story : La Flotte Électrique de GreenDelivery',
      description: 'Comment GreenDelivery a réduit ses coûts de 40% et amélioré son efficacité en passant à une flotte de motos électriques.',
      date: '1 Mai 2024',
      author: 'Omar Sy',
      readTime: '12 min',
      image: 'https://images.pexels.com/photos/8993561/pexels-photo-8993561.jpeg',
      category: 'Études de Cas'
    },
    {
      id: 'guide-2',
      type: 'guide',
      title: 'Optimiser l\'Autonomie de Votre Moto Électrique',
      description: 'Guide pratique pour maximiser l\'autonomie de votre batterie et adopter les meilleures pratiques de recharge.',
      date: '28 Avril 2024',
      author: 'Tech Team Proxy',
      readTime: '8 min',
      image: 'https://images.pexels.com/photos/110844/pexels-photo-110844.jpeg',
      category: 'Guide Technique'
    },
    {
      id: 'article-3',
      type: 'article',
      title: 'Impact Environnemental des Motos Électriques',
      description: 'Analyse de l\'empreinte carbone des motos électriques et leur contribution à la lutte contre le changement climatique.',
      date: '25 Avril 2024',
      author: 'Dr. Fatou Ndiaye',
      readTime: '7 min',
      image: 'https://images.pexels.com/photos/1108572/pexels-photo-1108572.jpeg',
      category: 'Environnement'
    }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesFilter = activeFilter === 'all' || resource.type === activeFilter;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'article':
        return <FileText size={20} className="text-blue-500" />;
      case 'guide':
        return <BookOpen size={20} className="text-green-500" />;
      case 'case-study':
        return <FileSpreadsheet size={20} className="text-orange-500" />;
      default:
        return <FileText size={20} />;
    }
  };

  return (
    <div className="pt-20">
      <Helmet>
        <title>Ressources | Proxy Emobility - Guides et Articles sur la Mobilité Électrique</title>
        <meta name="description" content="Explorez nos ressources sur la mobilité électrique en Afrique : guides pratiques, articles, études de cas et analyses économiques et environnementales." />
        <meta name="keywords" content="ressources mobilité électrique, guides motos électriques, études de cas, impact environnemental, économies, recharge, autonomie, Afrique" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/resources" />
        
        {/* Schema.org structured data for Article */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": "Centre de Ressources Proxy Emobility",
              "description": "Explorez nos ressources sur la mobilité électrique en Afrique : guides pratiques, articles, études de cas et analyses.",
              "url": "https://proxyemobility.netlify.app/resources",
              "publisher": {
                "@type": "Organization",
                "name": "Proxy Emobility",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://proxyemobility.netlify.app/proxy-logo.png"
                }
              },
              "mainEntity": {
                "@type": "ItemList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                      "@type": "Article",
                      "headline": "La Révolution de la Mobilité Électrique en Afrique",
                      "description": "Découvrez comment les motos électriques transforment le paysage urbain africain, réduisent les émissions et créent de nouvelles opportunités économiques.",
                      "image": "https://images.pexels.com/photos/3819830/pexels-photo-3819830.jpeg",
                      "author": {
                        "@type": "Person",
                        "name": "Dr. Amadou Diallo"
                      },
                      "publisher": {
                        "@type": "Organization",
                        "name": "Proxy Emobility",
                        "logo": {
                          "@type": "ImageObject",
                          "url": "https://proxyemobility.netlify.app/proxy-logo.png"
                        }
                      },
                      "datePublished": "2024-05-15"
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                      "@type": "TechArticle",
                      "headline": "Guide Complet des Motos Électriques",
                      "description": "Tout ce que vous devez savoir sur les motos électriques : technologie, autonomie, recharge, entretien et conseils d'utilisation.",
                      "image": "https://images.pexels.com/photos/13035553/pexels-photo-13035553.jpeg",
                      "author": {
                        "@type": "Person",
                        "name": "Sarah Koné"
                      },
                      "publisher": {
                        "@type": "Organization",
                        "name": "Proxy Emobility",
                        "logo": {
                          "@type": "ImageObject",
                          "url": "https://proxyemobility.netlify.app/proxy-logo.png"
                        }
                      },
                      "datePublished": "2024-05-10"
                    }
                  }
                ]
              }
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Centre de Ressources
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Explorez nos guides, articles et études de cas pour tout savoir sur la mobilité électrique et ses avantages pour l'Afrique.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-white border-b sticky top-20 z-10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-lg w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une ressource..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  activeFilter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                Tout
              </button>
              <button
                onClick={() => setActiveFilter('article')}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  activeFilter === 'article'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                Articles
              </button>
              <button
                onClick={() => setActiveFilter('guide')}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  activeFilter === 'guide'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                Guides
              </button>
              <button
                onClick={() => setActiveFilter('case-study')}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  activeFilter === 'case-study'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                Études de Cas
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          {filteredResources.length === 0 ? (
            <div className="text-center py-12">
              <Filter size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune ressource trouvée</h3>
              <p className="text-gray-600">
                Essayez de modifier vos critères de recherche
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredResources.map((resource) => (
                <div key={resource.id} className="bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl hover:translate-y-[-4px] border border-gray-100">
                  <div className="aspect-w-16 aspect-h-9 relative overflow-hidden">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-full object-cover transition-transform hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {getTypeIcon(resource.type)}
                      <span className="text-sm font-medium text-gray-600 capitalize">{resource.type.replace('-', ' ')}</span>
                      <span className="text-xs px-2 py-1 bg-primary-50 text-primary-700 rounded-full">{resource.category}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">{resource.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{resource.description}</p>
                    
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <div className="flex items-center mr-4">
                        <Calendar size={16} className="mr-1" />
                        <span>{resource.date}</span>
                      </div>
                      <div className="flex items-center mr-4">
                        <User size={16} className="mr-1" />
                        <span>{resource.author}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock size={16} className="mr-1" />
                        <span>{resource.readTime}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/resources/${resource.id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
                      >
                        Lire plus
                        <ArrowRight size={16} className="ml-1" />
                      </Link>
                      
                      {resource.downloadUrl && (
                        <button
                          className="inline-flex items-center text-gray-600 hover:text-gray-900"
                          onClick={() => window.open(resource.downloadUrl, '_blank')}
                        >
                          <Download size={16} className="mr-1" />
                          PDF
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Restez Informé</h2>
            <p className="text-lg text-gray-600 mb-8">
              Abonnez-vous à notre newsletter pour recevoir nos dernières ressources et actualités sur la mobilité électrique.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
              <input 
                type="email" 
                placeholder="Votre adresse email" 
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors">
                S'abonner
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;